<template>
    <div>
        <ts-loading-banner :loading="loading">
            <form class="form-horizontal">
                <!-- Event Name -->
                <div class="row form-group">
                    <label class="col-form-label col-md-4 required">
                        {{ $t('eventManagement.eventName') }}
                    </label>
                    <div class="col-md-6">
                        <input class="form-control" v-model="model.event_name"
                            :class="{ 'is-invalid': errors.has('event_name') }" />
                    </div>
                    <div class="invalid-feedback" v-if="errors.has('event_name')">
                        {{ errors.first('event_name') }}
                    </div>
                </div>

                <!-- Event Location -->
                <div class="row form-group">
                    <label class="col-form-label col-md-4">
                        {{ $t('eventManagement.eventLocation') }}
                    </label>
                    <div class="col-md-6">
                        <input class="form-control" v-model="model.event_location"
                            :class="{ 'is-invalid': errors.has('event_location') }" />
                        <div class="invalid-feedback" v-if="errors.has('event_location')">
                            {{ errors.first('event_location') }}
                        </div>
                    </div>
                </div>

                <!-- Start Date -->
                <div class="row form-group">
                    <label class="col-form-label col-md-4">
                        {{ $t('eventManagement.startDate') }}
                    </label>
                    <div class="col-md-6">
                        <ts-date-picker-input v-model="model.start_date"
                            :class="{ 'is-invalid': errors.has('start_date') }" />
                        <div class="invalid-feedback" v-if="errors.has('start_date')">
                            {{ errors.first('start_date') }}
                        </div>
                    </div>
                </div>

                <!-- End Date -->
                <div class="row form-group">
                    <label class="col-form-label col-md-4">
                        {{ $t('eventManagement.endDate') }}
                    </label>
                    <div class="col-md-6">
                        <ts-date-picker-input v-model="model.end_date"
                            :class="{ 'is-invalid': errors.has('end_date') }" />
                    </div>
                    <div class="invalid-feedback" v-if="errors.has('end_date')">
                        {{ errors.first('end_date') }}
                    </div>
                </div>

                <!-- Max Persons -->
                <div class="row form-group">
                    <label class="col-form-label col-md-4">
                        {{ $t('eventManagement.maxPerson') }}
                    </label>
                    <div class="col-md-6">
                        <input class="form-control" v-model.number="model.max_persons"
                            :class="{ 'is-invalid': errors.has('max_persons') }" />
                    </div>
                    <div class="invalid-feedback" v-if="errors.has('max_persons')">
                        {{ errors.first('max_persons') }}
                    </div>
                </div>

                <!-- Description -->
                <div class="row form-group">
                    <label class="col-form-label col-md-4">
                        {{ $t('eventManagement.eventDescription') }}
                    </label>
                    <div class="col-md-6">
                        <textarea class="form-control" v-model="model.event_description" rows="4"
                            :class="{ 'is-invalid': errors.has('event_description') }"></textarea>
                    </div>
                    <div class="invalid-feedback" v-if="errors.has('event_description')">
                        {{ errors.first('event_description') }}
                    </div>
                </div>

                <!-- Attachment -->
                <div class="row form-group">
                    <label class="col-form-label col-md-4">
                        {{ $t('eventManagement.attachmentFile') }}
                    </label>
                    <div class="col-md-6">
                        <AttachFile v-model="model" :editData="editData" @uploading="isUploading = $event" />
                        <div class="invalid-feedback" v-if="errors.has('attach_file_list')">
                            {{ errors.first('attach_file_list') }}
                        </div>
                    </div>
                </div>

                <!-- Buttons -->
                <div class="row">
                    <div class="col-md-12 ts-mt-4 text-right">
                        <ts-button class="ts-mr-2" @click.prevent="$emit('close')" :disabled="saving || isUploading">
                            {{ $t('cancel') }}
                        </ts-button>

                        <ts-button v-if="!isUpdate" color="primary" class="ml-2" :waiting="saving"
                            :disabled="isUploading" @click.prevent="onSave">
                            {{ isUploading ? $t('Uploading...') : $t('save') }}
                        </ts-button>

                        <ts-button v-if="isUpdate" color="primary" :waiting="updating" :disabled="isUploading"
                            @click.prevent="onUpdate">
                            {{ isUploading ? $t('Uploading...') : $t('update') }}
                        </ts-button>
                    </div>
                </div>
            </form>
        </ts-loading-banner>
    </div>
</template>

<script>
import draggable from 'vuedraggable'
import { Errors } from 'form-backend-validation'
import moment from 'moment'
import { isEmpty } from 'lodash'
import AttachFile from './attach-file.vue'

export default {
    name: 'event-management-form',
    props: ['editData'],
    components: { draggable, AttachFile },

    data() {
        return {
            model: {
                event_mgt_id: '',
                event_name: null,
                event_description: null,
                event_location: null,
                start_date: moment().format('DD-MM-YYYY'),
                end_date: moment().format('DD-MM-YYYY'),
                max_persons: null,
                attach_file_list: []
            },
            errors: new Errors(),
            saving: false,
            updating: false,
            loading: false,
            isUploading: false
        }
    },

    computed: {
        isUpdate() {
            return !isEmpty(this.editData)
        }
    },

    created() {
        this.setEditData()
    },

    methods: {
        onSave() {
            this.errors = new Errors()
            this.saving = true

            // Check if uploading is still happening
            if (this.isUploading) {
                this.$toasted.error('Please wait until all files finish uploading.')
                this.saving = false
                return
            }

            const notUploaded = this.model.attach_file_list.filter(f => !f.uploaded)
            if (notUploaded.length > 0) {
                this.$toasted.error('Please wait until all files finish uploading.')
                this.saving = false
                return
            }

            const payload = { ...this.model }
            payload.start_date = moment(this.model.start_date, 'DD-MM-YY').format('YYYY-MM-DD')
            payload.end_date = moment(this.model.end_date, 'DD-MM-YY').format('YYYY-MM-DD')

            this.$store
                .dispatch('eventManagement/store', payload)
                .then(response => {
                    this.$toasted.success(response.message)
                    this.$emit('close')
                })
                .catch(error => {
                    this.errors = new Errors(error.errors)
                    this.$toasted.error(error.message)
                })
                .finally(() => {
                    this.saving = false
                })
        },

        onUpdate() {
            this.updating = true

            if (this.isUploading) {
                this.$toasted.error('Please wait until all files finish uploading.')
                this.updating = false
                return
            }

            const payload = { ...this.model }
            payload.start_date = moment(this.model.start_date, 'DD-MM-YY').format('YYYY-MM-DD')
            payload.end_date = moment(this.model.end_date, 'DD-MM-YY').format('YYYY-MM-DD')

            this.$store
                .dispatch('eventManagement/update', payload)
                .then(response => {
                    this.$toasted.success(response.message)
                    this.$emit('close')
                })
                .catch(error => {
                    this.errors = new Errors(error.errors)
                    this.$toasted.error(error.message)
                })
                .finally(() => {
                    this.updating = false
                })
        },

        setEditData() {
            if (!isEmpty(this.editData)) {
                this.model.event_name = this.editData.event_name
                this.model.event_description = this.editData.event_description
                this.model.event_location = this.editData.event_location
                this.model.max_persons = this.editData.max_persons
                this.model.start_date = this.editData.event_start_date
                this.model.end_date = this.editData.event_end_date
                this.model.event_mgt_id = this.editData.event_mgt_id
                this.model.attach_file_list = this.editData.event_attachments.map(el => ({
                    attach_file_path: el.attach_file_path,
                    storage_path: el.storage_path,
                    attach_file_name: el.attach_file_name,
                    uploaded: true
                }))
            }
        }
    }
}
</script>

<style scoped>
.bb {
    border: none;
    outline: none !important;
    padding: 3px 7px;
    border-radius: 5px;
}
</style>
